import { memo, useMemo, useRef, useContext, useEffect } from 'react';

import { Icon, SelectWrapper } from '@Sage/hoi-poi-ui';
import { generateUUID } from '@Sage/ssm-utils';

import {
    findParentAutomationNode,
    findSceneRelativePathByNodeId,
    getNextBoxCount,
    getSceneNextDotPathForSiblingBranch,
} from 'containers/settings/AutomationSettings/utils';
import { getLiteral } from 'utils/getLiteral';

import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import './styles.scss';

const VALUE_CONDITIONS_NEW_BRANCH = 'conditions_new_branch';

const AutomationBuilderAddNodesButton = memo(({ parentId, edgeTargetId }) => {
    const { setNodes, setEdges, data, onChange } = useContext(AutomationBuilderCanvasContext);
    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const addNodesButtonRef = useRef(null);
    const selectWrapperProps = useMemo(
        () => ({
            children: <Icon name="addPlus" />,
            popoverWide: Boolean(edgeTargetId),
            onChange: (value) => {
                const selected = value?.value;
                if (!selected) return;

                const scene = dataRef.current?.scene;
                if (!scene) return;

                const isNewBranchOption = selected === VALUE_CONDITIONS_NEW_BRANCH;
                if (isNewBranchOption && !edgeTargetId) return;
                const nodeType = isNewBranchOption ? 'conditions' : selected;
                const countKey = nodeType === 'conditions' ? 'conditions' : 'action';

                const newNodeId = generateUUID();
                const newNodeData = {
                    id: newNodeId,
                    type: nodeType,
                    name: nodeType === 'conditions' ? 'conditions_logic' : 'update_entity_related',
                    parameters: {},
                    next: [],
                };

                let appendPathDot;
                let edgeSourceId;
                /** append | spliceReplaceTargetUnderSource */
                let insertKind = 'append';
                let spliceSourceId;
                let spliceTargetId;

                if (edgeTargetId) {
                    const sourceRel = findSceneRelativePathByNodeId(scene, parentId);
                    if (isNewBranchOption) {
                        appendPathDot = sourceRel ? `scene.${sourceRel}.next` : 'scene.next';
                        edgeSourceId = parentId;
                    } else {
                        insertKind = 'spliceReplaceTargetUnderSource';
                        appendPathDot = sourceRel ? `scene.${sourceRel}.next` : 'scene.next';
                        spliceSourceId = parentId;
                        spliceTargetId = edgeTargetId;
                        edgeSourceId = parentId;
                    }
                } else {
                    const leafRel = findSceneRelativePathByNodeId(scene, parentId);
                    if (leafRel == null) return;
                    if (isNewBranchOption) {
                        appendPathDot = getSceneNextDotPathForSiblingBranch(leafRel);
                        const parentNode = findParentAutomationNode(scene, parentId);
                        if (!parentNode?.id) return;
                        edgeSourceId = parentNode.id;
                    } else {
                        insertKind = 'append';
                        appendPathDot = leafRel ? `scene.${leafRel}.next` : 'scene.next';
                        edgeSourceId = parentId;
                    }
                }

                onChange(appendPathDot, (currentNext) => {
                    const position = getNextBoxCount(countKey);
                    const safe = Array.isArray(currentNext) ? currentNext : [];

                    if (insertKind === 'spliceReplaceTargetUnderSource') {
                        const i = safe.findIndex((n) => n.id === spliceTargetId);
                        if (i === -1) {
                            return [...safe, { ...newNodeData, position, next: [] }];
                        }
                        const t = safe[i];
                        return [
                            ...safe.slice(0, i),
                            { ...newNodeData, position, next: [t] },
                            ...safe.slice(i + 1),
                        ];
                    }

                    return [...safe, { ...newNodeData, position, next: [] }];
                });

                setTimeout(() => {
                    const newNode = {
                        id: newNodeId,
                        position: { x: 0, y: 0 },
                        style: { opacity: 0 },
                        type: nodeType,
                        name:
                            nodeType === 'conditions'
                                ? 'conditions_logic'
                                : 'update_entity_related',
                        data: {},
                    };

                    setNodes((prevNodes) => [...prevNodes, newNode]);

                    setEdges((prevEdges) => {
                        let nextEdges = [...prevEdges];

                        if (insertKind === 'spliceReplaceTargetUnderSource') {
                            nextEdges = nextEdges.filter(
                                (e) =>
                                    !(e.source === spliceSourceId && e.target === spliceTargetId),
                            );
                            nextEdges.push({
                                id: `edge-${spliceSourceId}-${newNodeId}`,
                                source: spliceSourceId,
                                target: newNodeId,
                                hidden: true,
                                type: 'buttonedge',
                            });
                            nextEdges.push({
                                id: `edge-${newNodeId}-${spliceTargetId}`,
                                source: newNodeId,
                                target: spliceTargetId,
                                hidden: true,
                                type: 'buttonedge',
                            });
                            return nextEdges;
                        }

                        return [
                            ...nextEdges,
                            {
                                id: `edge-${edgeSourceId}-${newNodeId}`,
                                source: edgeSourceId,
                                target: newNodeId,
                                hidden: true,
                                type: 'buttonedge',
                            },
                        ];
                    });
                });

                addNodesButtonRef.current?.querySelector('span')?.click();
            },
            options: [
                {
                    label: getLiteral('label_condition'),
                    iconType: 'conditions',
                    value: 'conditions',
                },
                {
                    label: getLiteral('label_action'),
                    iconType: 'automations',
                    value: 'action',
                },
                ...(edgeTargetId
                    ? [
                          {
                              label: getLiteral('label_new_branch'),
                              iconType: 'lineAdd',
                              value: VALUE_CONDITIONS_NEW_BRANCH,
                          },
                      ]
                    : []),
            ],
        }),
        [edgeTargetId, onChange, parentId, setEdges, setNodes],
    );

    return (
        <div className="fm-automation-builder__add-nodes-button" ref={addNodesButtonRef}>
            <SelectWrapper {...selectWrapperProps} />
        </div>
    );
});

export default AutomationBuilderAddNodesButton;
